import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useState, Fragment, lazy, Suspense } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { api } from '../lib/api';
import SEO from '../components/SEO';
import CampCard from '../components/CampCard';
import { supabase } from '../lib/supabase';

// Lazy load admin overlay to prevent MDX editor from being bundled in SSR
const AdminCampOverlay = lazy(() => import('../components/AdminCampOverlay'));
import toast from 'react-hot-toast';
import type { Camp } from '../types/database';
import LoadingState from '../components/LoadingState';

export default function CityPage() {
  const { stateSlug, citySlug } = useParams<{ stateSlug: string; citySlug: string }>();
  const navigate = useNavigate();
  
  // Edit overlay state
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [isEditOverlayOpen, setIsEditOverlayOpen] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ camp: Camp; skipForever: boolean } | null>(null);

  // Get state and city names from slugs
  const getStateName = (slug: string) => {
    return slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getCityName = (slug: string) => {
    return slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const stateName = stateSlug ? getStateName(stateSlug) : '';
  const cityName = citySlug ? getCityName(citySlug) : '';

  const { data: camps = [], isLoading, error, refetch } = useQuery({
    queryKey: ['camps-by-city', stateSlug, citySlug],
    queryFn: () => (stateSlug && citySlug) ? api.locations.getCampsByCity(cityName, stateName) : Promise.resolve([]),
    enabled: !!(stateSlug && citySlug),
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });

  // Edit handler
  const handleEditCamp = (camp: Camp) => {
    setSelectedCamp(camp);
    setIsEditOverlayOpen(true);
  };

  const handleEditSave = () => {
    setIsEditOverlayOpen(false);
    setSelectedCamp(null);
    refetch(); // Refresh the camps list
  };

  const handleDeleteCamp = () => {
    if (!selectedCamp) return;
    setDeleteConfirmation({ camp: selectedCamp, skipForever: true });
    setIsEditOverlayOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;

    const { camp: campToDelete, skipForever } = deleteConfirmation;
    
    try {
      // Add to skipped camps if requested
      if (skipForever) {
        const { error: skipError } = await supabase
          .from('skipped_camps')
          .insert({
            name: campToDelete.name,
            website_url: campToDelete.website_url,
            slug: campToDelete.slug,
            reason: 'Deleted by admin'
          });

        if (skipError) throw skipError;
      }

      // Delete related records first
      const { error: favoritesError } = await supabase
        .from('camp_favorites')
        .delete()
        .eq('camp_id', campToDelete.id);

      if (favoritesError) throw favoritesError;

      // Delete images from Supabase Storage before removing database records
      try {
        const { data: images } = await supabase
          .from('camp_images')
          .select('url')
          .eq('camp_id', campToDelete.id);
        
        if (images && images.length > 0) {
          // Extract file paths from URLs and delete from storage
          for (const image of images) {
            try {
              const url = new URL(image.url);
              const storagePath = url.pathname.substring(1); // Remove leading slash
              
              const { error: removeError } = await supabase.storage
                .from('camp-images')
                .remove([storagePath]);
              
              if (removeError) {
                console.warn(`Warning: Failed to delete storage file ${storagePath}:`, removeError);
              }
            } catch (storageError) {
              console.warn(`Warning: Failed to process image URL ${image.url}:`, storageError);
            }
          }
        }
      } catch (storageError) {
        console.warn('Warning: Failed to delete some storage files:', storageError);
        // Continue with deletion even if storage cleanup fails
      }

      const { error: imagesError } = await supabase
        .from('camp_images')
        .delete()
        .eq('camp_id', campToDelete.id);

      if (imagesError) throw imagesError;

      const { error: categoryError } = await supabase
        .from('camp_category_relations')
        .delete()
        .eq('camp_id', campToDelete.id);

      if (categoryError) throw categoryError;

      // Finally delete the camp
      const { error: campError } = await supabase
        .from('camps')
        .delete()
        .eq('id', campToDelete.id);

      if (campError) throw campError;

      const message = skipForever 
        ? 'Camp deleted and added to skip list to prevent future imports'
        : 'Camp deleted successfully';
      toast.success(message);
      
      setDeleteConfirmation(null);
      setSelectedCamp(null);
      refetch(); // Refresh the camps list
    } catch (error) {
      console.error('Error deleting camp:', error);
      toast.error('Failed to delete camp');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
        <SEO
          title={`Summer Camps in ${cityName}, ${stateName} - CampWhiz.com`}
          description={`Find summer camps in ${cityName}, ${stateName}. Browse local camps with detailed information and reviews.`}
          keywords={`summer camps ${cityName} ${stateName}, local camps ${cityName}, camps near me ${cityName}`}
          url={`/camps-by-city/${stateSlug}/${citySlug}`}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <LoadingState message={`Loading camps in ${cityName}, ${stateName}...`} fullPage />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
        <SEO
          title={`Summer Camps in ${cityName}, ${stateName} - CampWhiz.com`}
          description={`Find summer camps in ${cityName}, ${stateName}. Browse local camps with detailed information and reviews.`}
          keywords={`summer camps ${cityName} ${stateName}, local camps ${cityName}, camps near me ${cityName}`}
          url={`/camps-by-city/${stateSlug}/${citySlug}`}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-semibold text-gray-900 mb-4">Error Loading Camps</h1>
            <p className="text-gray-600 mb-6">There was an error loading the camps in {cityName}, {stateName}. Please try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SEO
        title={`Summer Camps in ${cityName}, ${stateName} - CampWhiz.com`}
        description={`Find summer camps in ${cityName}, ${stateName}. Browse local camps with detailed information and reviews.`}
        keywords={`summer camps ${cityName} ${stateName}, local camps ${cityName}, camps near me ${cityName}`}
        url={`/camps-by-city/${stateSlug}/${citySlug}`}
      />
      
      <div className="page-content">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
            Summer Camps in {cityName}, {stateName}
          </h1>
          <div className="space-y-4 max-w-3xl mx-auto">
            <p className="text-xl text-gray-600">
              Discover {camps.length} summer camp{camps.length !== 1 ? 's' : ''} in {cityName}. Find the perfect camp for your child.
            </p>
            <p className="text-gray-600">
              Browse through our curated selection of summer camps in {cityName}, {stateName}. Each camp offers unique activities, experienced staff, and enriching experiences to help your child grow, learn, and create lasting memories.
            </p>
            <p className="text-gray-600">
              Our detailed camp listings include information about activities, age groups, schedules, and facilities to help you make an informed decision for your child's summer adventure.
            </p>
          </div>
        </div>

        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/camps-by-city/${stateSlug}`)}
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to {stateName}
          </button>
        </div>

        {/* Camps Grid */}
        {camps.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {camps.map((camp: Camp) => (
              <CampCard 
                key={camp.id} 
                camp={camp} 
                onEdit={handleEditCamp}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <MapPinIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Camps Found in {cityName}</h3>
            <div className="space-y-4 max-w-2xl mx-auto">
              <p className="text-gray-600">
                We're currently expanding our database of summer camps in {cityName}, {stateName}. While we don't have any active camps listed at the moment, we encourage you to:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2">
                <li>Check back regularly as we add new camps frequently</li>
                <li>Explore camps in nearby cities within {stateName}</li>
                <li>Sign up for notifications to be alerted when new camps are added in your area</li>
              </ul>
              <p className="text-gray-600">
                If you know of any summer camps in {cityName} that should be listed here, please contact us to help us grow our community.
              </p>
            </div>
            <button
              onClick={() => navigate(`/camps-by-city/${stateSlug}`)}
              className="btn btn-primary"
            >
              View Other Cities in {stateName}
            </button>
          </div>
        )}
      </div>

      {/* Edit Overlay */}
      {isEditOverlayOpen && selectedCamp && (
        <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div></div>}>
          <AdminCampOverlay
            camp={selectedCamp}
            isOpen={isEditOverlayOpen}
            onClose={handleEditSave}
            onSave={handleEditSave}
            onDelete={handleDeleteCamp}
          />
        </Suspense>
      )}

      {/* Delete Confirmation Modal */}
      <Transition.Root show={!!deleteConfirmation} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setDeleteConfirmation(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Delete Camp
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to remove{' '}
                          <span className="font-medium">{deleteConfirmation?.camp.name}</span>{' '}
                          from your camp listings? <span className="font-semibold text-gray-700">This action cannot be undone.</span>
                        </p>
                        <div className="mt-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={deleteConfirmation?.skipForever || false}
                              onChange={(e) => {
                                if (deleteConfirmation) {
                                  setDeleteConfirmation({
                                    ...deleteConfirmation,
                                    skipForever: e.target.checked
                                  });
                                }
                              }}
                              className="rounded border-gray-300 text-red-600 shadow-sm focus:border-red-500 focus:ring-red-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">
                              Add to skip list to prevent future imports
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="btn-danger sm:ml-3 sm:w-auto"
                      onClick={handleDeleteConfirm}
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      className="btn-outline mt-3 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteConfirmation(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
